import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Environment } from 'src/environments/environment.model';

import {
  CopyCropFieldsToManureSpaceResponse,
  CropField,
  CropFieldType,
  GetCropFieldsResponse,
  GetMostUsedCropsResponse,
  MostUsedCropReference,
  UpsertCropFieldResponse,
  UpsertCropFieldsWithRegulatoryParcelResponse,
} from '../models';
import {
  ValidateCropFieldGeometryRequest,
  ValidateCropFieldGeometryResponse,
} from '../models/validate-crop-field-geometry.model';

@Injectable({
  providedIn: 'root',
})
export class CropFieldService {
  private readonly http = inject(HttpClient);
  private readonly env = inject<Environment>(EnvironmentInjector);

  private readonly endpoints = {
    arableAdvice: `${this.env.APIM_BASE_URL}/arable-advice-service/crop-fields`,
  };

  private readonly baseUrl = this.buildBaseUrl();

  private buildBaseUrl(): string {
    return this.endpoints.arableAdvice;
  }

  getCropFields(cropYear: number, cropFieldType: CropFieldType): Observable<CropField[]> {
    return this.http
      .get<GetCropFieldsResponse>(this.baseUrl + '/cropyear/' + cropYear + '/type/' + cropFieldType)
      .pipe(map((d) => d.cropFields.map((cropField) => ({ ...cropField }))));
  }

  upsertCropField(cropField: CropField) {
    return this.http.post<UpsertCropFieldResponse>(this.baseUrl, cropField, {});
  }

  upsertCropFieldsWithRegulatoryParcel(cropFields: CropField[]) {
    return this.http.post<UpsertCropFieldsWithRegulatoryParcelResponse>(
      this.baseUrl + '/with-regulatory-parcels',
      cropFields,
      {},
    );
  }

  deleteCropField(cropFieldId: number) {
    return this.http.delete<unknown>(this.baseUrl + '/' + cropFieldId);
  }

  updateMachiningDepth(id: number, machiningDepthInCentimeters: number) {
    return this.http.post(`${this.baseUrl}/${id}/machiningdepth`, { machiningDepthInCentimeters });
  }

  copyCropFieldsToManureSpace(cropYear: number): Observable<CopyCropFieldsToManureSpaceResponse> {
    return this.http.post<CopyCropFieldsToManureSpaceResponse>(
      `${this.baseUrl}/copy-fields-to-manure-space`,
      { cropYear },
    );
  }

  getMostUsedCropReferences(cropFieldType: CropFieldType): Observable<MostUsedCropReference[]> {
    return this.http
      .get<GetMostUsedCropsResponse>(`${this.baseUrl}/type/${cropFieldType}/mostusedcrops`)
      .pipe(map((d) => d.mostUsedCrops));
  }

  getCropFieldsForCustomer(
    cropYear: number,
    cropFieldType: CropFieldType,
    customerId: number,
  ): Observable<CropField[]> {
    return this.http
      .get<GetCropFieldsResponse>(
        `${this.baseUrl}/year/${cropYear}/type/${cropFieldType}/customerId/${customerId}`,
      )
      .pipe(map((d) => d.cropFields.map((cropField) => ({ ...cropField }))));
  }

  /**
   * Validates crop field geometry against geo-layers without persisting
   * Returns suggested values for soil type, water authority, and environmental flags
   */
  validateGeometry(
    request: ValidateCropFieldGeometryRequest,
  ): Observable<ValidateCropFieldGeometryResponse> {
    return this.http.post<ValidateCropFieldGeometryResponse>(
      `${this.baseUrl}/validate-geometry`,
      request,
    );
  }
}
